import React from 'react';
import { CheckCircle2, Users, Target } from 'lucide-react';

function AboutUs() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="py-12 md:py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 space-y-10 md:space-y-12">
          {/* Page Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a4a1a] mb-4 leading-tight">
              About Us
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600 mx-auto rounded-full"></div>
          </div>
          
          {/* Introduction Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 lg:p-12 border border-slate-100">
            <div className="space-y-6">
              <div className="flex items-start gap-4 md:gap-6">
                <div className="flex-shrink-0">
                  <img 
                    src="/CRED-free.png" 
                    alt="HalalCred Logo" 
                    className="h-[45px] md:h-[50px] w-auto"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
                    <strong className="text-[#1a4a1a] font-bold">HalalCred</strong> is a fintech startup launched in 2025. It operates under the guidance and mentorship of esteemed Shariah scholars led by <strong className="text-[#1a4a1a]">Mufti Ibrahim Essa</strong>, a faculty member at Darul Ifta, Jamiah Darul Uloom Karachi, Pakistan.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Strategy Section */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 lg:p-12 border border-slate-100">
            <div className="space-y-8">
              {/* Section Header */}
              <div className="border-b-2 border-emerald-100 pb-4">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a4a1a] leading-tight">
                  Islamic Financing 2.0 — Delivered Through CDFIs
                </h2>
              </div>
              
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed font-medium">
                <strong className="text-[#1a4a1a]">HalalCred</strong> is the public-facing platform for a national Islamic financing strategy developed with Craft3 to:
              </p>

              {/* Strategic Goals Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 my-8">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 md:p-8 border-2 border-emerald-100 hover:border-emerald-200 transition-all group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-base md:text-lg text-slate-800 leading-relaxed font-semibold pt-1">
                      Standardize Islamic-compliant financing
                    </p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 md:p-8 border-2 border-emerald-100 hover:border-emerald-200 transition-all group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-base md:text-lg text-slate-800 leading-relaxed font-semibold pt-1">
                      Reduce legal and operational barriers for CDFIs
                    </p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 md:p-8 border-2 border-emerald-100 hover:border-emerald-200 transition-all group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-base md:text-lg text-slate-800 leading-relaxed font-semibold pt-1">
                      Expand access to riba-free capital nationwide
                    </p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 md:p-8 border-2 border-emerald-100 hover:border-emerald-200 transition-all group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-base md:text-lg text-slate-800 leading-relaxed font-semibold pt-1">
                      Keep capital rooted in community impact
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Statements */}
              <div className="bg-gradient-to-r from-emerald-600 to-[#1a4a1a] rounded-2xl p-8 md:p-10 text-white mt-8">
                <div className="space-y-4">
                  <p className="text-xl md:text-2xl font-bold leading-relaxed">
                    This is not a startup experimenting with faith-based finance.
                  </p>
                  <p className="text-xl md:text-2xl font-bold leading-relaxed">
                    This is institutional experience made accessible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Our Partnership Section */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 lg:p-12 border border-slate-100">
            <div className="space-y-8">
              {/* Section Header */}
              <div className="border-b-2 border-emerald-100 pb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 md:w-7 md:h-7 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a4a1a] leading-tight">
                    Our Partnership
                  </h2>
                </div>
              </div>
              
              <div className="space-y-6">
                <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
                  HalalCred was selected as the platform for Islamic Financing 2.0 because it aligns technology, community access, and credibility.
                </p>

                <div className="bg-gradient-to-br from-slate-50 to-emerald-50 rounded-2xl p-6 md:p-8 border border-slate-200">
                  <div className="flex items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <img 
                        src="/craft-3.png" 
                        alt="Craft3 Logo" 
                        className="h-16 w-16 md:h-20 md:w-20 object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
                        We operate in strategic partnership with <strong className="text-[#1a4a1a] font-bold">Craft3</strong>, a nonprofit Community Development Financial Institution founded in 1994 and certified by the U.S. Treasury.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-2xl p-8 md:p-10 border-2 border-emerald-200">
                  <h3 className="text-xl md:text-2xl font-bold text-[#1a4a1a] mb-6 flex items-center gap-3">
                    <img 
                      src="/craft-3.png" 
                      alt="Craft3 Logo" 
                      className="h-8 w-8 md:h-10 md:w-10 object-contain"
                    />
                    Craft3:
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-base md:text-lg text-slate-800 leading-relaxed font-medium pt-0.5">
                        Has been delivering Islamic-compliant financing since 2019
                      </p>
                    </div>
                    <div className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-base md:text-lg text-slate-800 leading-relaxed font-medium pt-0.5">
                        Serves underserved communities across the Pacific Northwest
                      </p>
                    </div>
                    <div className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-base md:text-lg text-slate-800 leading-relaxed font-medium pt-0.5">
                        Works with banks, foundations, and impact investors
                      </p>
                    </div>
                    <div className="flex items-start gap-3 group">
                      <div className="flex-shrink-0 w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-base md:text-lg text-slate-800 leading-relaxed font-medium pt-0.5">
                        Has demonstrated that Islamic financing works at institutional scale
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-600 to-[#1a4a1a] rounded-2xl p-6 md:p-8 text-white text-center">
                  <p className="text-lg md:text-xl font-bold leading-relaxed">
                    HalalCred exists to extend that success nationally.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
